import fp from 'lodash/fp';
import React, { ComponentType, useState, useEffect, useContext } from 'react';

import { usePrevious } from 'Modules/ui/hooks';

import { contextHOC } from '../../utils';


export type SizeContextProps = {
    rem?: number;
    viewportWidth?: number;
    viewportHeight?: number;
};

const context = React.createContext<SizeContextProps>({});


const getSizeContextValues = () => ({
    rem: parseInt(window.getComputedStyle(document.documentElement, null)
        .getPropertyValue('font-size'), 10),

    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
});

export const SizeContextProvider: ComponentType = ({ children }) => {
    const [value, setValue] = useState(getSizeContextValues);

    useEffect(() => {
        const updateValues = fp.debounce(100, () => setValue(getSizeContextValues));
        window.addEventListener('resize', updateValues);
        updateValues();
        return () => {
            updateValues.cancel();
            window.removeEventListener('resize', updateValues);
        };
    }, []);

    return (
        <context.Provider value={value}>
            {children}
        </context.Provider>
    );
};

export const SizeContext = context.Consumer;

export const withSizeContext = contextHOC(context.Consumer);

export const useSizeContext = (): SizeContextProps => useContext(context);

export const useRemUpdatedCallback = (callback: () => void) => {
    const { rem } = useSizeContext();
    const oldRem = usePrevious(rem);

    useEffect(
        () => {
            if (rem !== oldRem)
                callback();
        },
        [rem, oldRem, callback],
    );
};
